/**
 * formatLegalDocument — RPC contract for the AI legal-document
 * format-and-translate callable.
 *
 * Cross-process contract between the Settings → Legal editor (the
 * `web/src/legal/` callable wrapper) and
 * `firebase/src/legal/formatLegalDocument.webhook.ts` (onCall
 * handler, wrapping `formatLegalDocument.chat.ts`'s one `chat()` seam).
 * Both ends parse with `.strict()` — an extra key fails the whole
 *
 * The operator receives one legal document in one language from their
 * lawyer; the storefront serves every locale `@momo/i18n`'s
 * `LocaleEnum` carries, and a document kind publishes only once BOTH
 * are present (`@momo/i18n`'s `resolveLegalDocument`). This callable is
 * the seam that gets an operator from "one pasted document" to "every
 * locale, in the typed block shape every reader already renders" — a
 * draft the operator still reviews and publishes themselves
 * (`web/src/admin/settings/legal/legal.sync.ts`'s own
 * `publishLegalDocument` write is untouched by this file — nothing this
 * callable returns reaches a published document without that separate,
 * human-triggered write).
 *
 * `LegalBlockSchema`/`LegalSectionSchema` below are the schema-validated
 * twin of `@momo/i18n`'s `LegalBlock`/`LegalSection` — that module
 * exports the shape as plain TS types, not zod, so there is nothing to
 * import at this LLM-output boundary. The two are structurally
 * identical field-for-field, so a value parsed here satisfies every
 * existing `LegalSection[]` consumer (`legal-document.view.ts`) with no
 * cast.
 *
 * Fields justified:
 *   kind          — which document this is (terms/privacy), so the
 *                    model renders each locale in that document's own
 *                    legal register; `@momo/i18n`'s shared
 *                    `LegalDocumentKindSchema`, never a second enum.
 *   sourceText    — the operator's pasted prose, one language, already
 *                    bounded to `LegalContentSchema`'s own 65536-char
 *                    cap so a formatted draft can always be published
 *                    without a second validation surprise.
 *   targetLocales — every locale a published document must exist in,
 *                    source language included — data, not a hardcoded
 *                    locale pair, so a third locale is additive here
 *                    too.
 *
 * Output:
 *   detectedLocale  — the source text's own language, surfaced back to
 *                     the editor as feedback; never trusted for anything
 *                     the operator did not paste.
 *   detectedVersion — the version stamp the source document itself
 *                     carries, read out of its prose. Nullish, and
 *                     ordinarily so: plenty of documents carry no stamp,
 *                     and the editor falls back to its own value for
 *                     those. What it must never be is a plausible date
 *                     the model composed — an invented stamp becomes the
 *                     published version identifier of a legal document
 *                     and reads exactly like a real one, so nothing
 *                     downstream can catch it and the operator has no
 *                     reason to look.
 *   documents       — one structured document per requested locale.
 *
 * Excluded:
 *   a normalised date — `detectedVersion` is the identifier as the
 *     document states it, not a parsed timestamp. Whatever the document
 *     calls its own edition is what readers see beside the text, and it
 *     is what the operator recognises as theirs; nothing computes with
 *     it. Normalising would also invite the model to reformat, which is
 *     one step from composing, and the whole hazard here is composition.
 *
 * Organization context is explicit in the request. The server validates
 * membership or possession of the scoped access token independently.
 */
import { LegalDocumentKindSchema, LocaleEnum } from "@momo/i18n";
import { z } from "zod";
import { Id } from "./workspace.entity.ts";

/**
 * The schema-validated twin of `@momo/i18n`'s `LegalBlock` type — see
 * this file's own header for why the twin exists instead of an import.
 * `.min(1)` on every text-bearing field is the structural half of "no
 * empty block": a block the model emits with nothing in it fails to
 * parse rather than reaching a reader as a blank paragraph or list.
 *
 * Both discriminators carry a `.describe()` naming their literal token,
 * because this union is an LLM output boundary and `"p"` is an
 * abbreviation no model infers from the concept: asked for a paragraph
 * with the token unnamed, Gemini emits `"paragraph"` and every response
 * fails the union post-hoc. `"list"` binds only because the token
 * happens to be the English word — luck this description removes the
 * reliance on.
 */
export const LegalBlockSchema = z.discriminatedUnion("kind", [
	z.object({
		kind: z
			.literal("p")
			.describe('a paragraph block: the single letter "p", never "paragraph"'),
		text: z
			.string()
			.min(1)
			.describe("one paragraph of the section's body text"),
	}),
	z.object({
		kind: z.literal("list").describe('a list block: the exact word "list"'),
		items: z
			.array(z.string().min(1))
			.min(1)
			.describe("the list's items, in source order"),
	}),
]);
export type LegalBlockOutput = z.infer<typeof LegalBlockSchema>;

/**
 * The schema-validated twin of `@momo/i18n`'s `LegalSection` type.
 * `.min(1)` on `blocks` is the structural half of "no empty section".
 */
export const LegalSectionSchema = z.object({
	heading: z
		.string()
		.min(1)
		.describe(
			"the section's flat, sequentially numbered topical label — one number, a period, a short noun phrase naming what the section covers; never a full sentence, never nested",
		),
	blocks: z
		.array(LegalBlockSchema)
		.min(1)
		.describe("the section's paragraphs and lists, in source order"),
});
export type LegalSectionOutput = z.infer<typeof LegalSectionSchema>;

export const FormatLegalDocumentInput = z
	.object({
		orgId: Id,
		kind: LegalDocumentKindSchema,
		sourceText: z
			.string()
			.min(1)
			.max(65536)
			.describe(
				"the operator's pasted legal-document prose, in one source language",
			),
		targetLocales: z
			.array(LocaleEnum)
			.min(1)
			.describe(
				"every locale a published document must exist in, source language included",
			),
	})
	.strict();
export type FormatLegalDocumentInput = z.infer<typeof FormatLegalDocumentInput>;

export const FormatLegalDocumentOutput = z
	.object({
		detectedLocale: LocaleEnum.describe(
			"the source text's own detected language",
		),
		detectedVersion: z
			.string()
			.min(1)
			.nullish()
			.describe(
				"the version or effective-date stamp the source document states about itself, written the way the document writes it, with the labelling word that introduces it dropped and the identifier left otherwise untouched. Null whenever the document states none, which is the ordinary case for a legal document and always preferable to a stamp composed to look like one",
			),
		documents: z
			.array(
				z.object({
					locale: LocaleEnum,
					sections: z.array(LegalSectionSchema).min(1),
				}),
			)
			.min(1)
			.describe("one structured document per requested locale"),
	})
	.strict();
export type FormatLegalDocumentOutput = z.infer<
	typeof FormatLegalDocumentOutput
>;
